import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

const InvoiceForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState({
        clientName: '',
        clientContact: '', // Menggunakan clientContact
        clientAddress: '',
        dueDate: '',
        items: [{ description: '', quantity: 1, price: 0 }],
        tax: 11,
        notes: '',
        status: 'Draft',
    });
    const [totals, setTotals] = useState({ subTotal: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditMode) {
            setLoading(true);
            const fetchInvoice = async () => {
                const token = localStorage.getItem('token');
                try {
                    const config = { headers: { 'x-auth-token': token } };
                    const res = await axios.get(`https://planstopia-production.up.railway.app/api/invoices/${id}`, config);
                    const { dueDate, ...rest } = res.data;
                    setFormData({ ...rest, dueDate: new Date(dueDate).toISOString().split('T')[0] });
                } catch (err) {
                    setError('Gagal memuat data invoice.');
                } finally {
                    setLoading(false);
                }
            };
            fetchInvoice();
        }
    }, [id, isEditMode]);

    useEffect(() => {
        const subTotal = formData.items.reduce((acc, item) => acc + (Number(item.quantity) * Number(item.price)), 0);
        const total = subTotal + (subTotal * (Number(formData.tax) / 100));
        setTotals({ subTotal, total });
    }, [formData.items, formData.tax]);

    const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleItemChange = (index, e) => {
        const newItems = [...formData.items];
        newItems[index][e.target.name] = e.target.value;
        setFormData({ ...formData, items: newItems });
    };

    const addItem = () => setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, price: 0 }] });
    const removeItem = (index) => setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };
        const submissionData = { ...formData, ...totals };
        try {
            if (isEditMode) {
                await axios.put(`https://planstopia-production.up.railway.app/api/invoices/${id}`, submissionData, config);
            } else {
                await axios.post('https://planstopia-production.up.railway.app/api/invoices', submissionData, config);
            }
            navigate('/invoices');
        } catch (err) {
            setError(err.response?.data?.msg || 'Terjadi kesalahan.');
        } finally {
            setLoading(false);
        }
    };
    
    if (loading && isEditMode) return <div><h2>Memuat formulir...</h2></div>;

    return (
        <div>
            <form onSubmit={handleSubmit} style={styles.form}>
                <h2>{isEditMode ? 'Edit Invoice' : 'Buat Invoice Baru'}</h2>
                
                <div style={styles.section}>
                    <h4>Informasi Klien</h4>
                    <input name="clientName" value={formData.clientName} onChange={handleFormChange} placeholder="Nama Klien" required style={styles.input} />
                    <input name="clientContact" value={formData.clientContact} onChange={handleFormChange} placeholder="Kontak Klien (Nama/No. HP)" required style={styles.input} />
                    <textarea name="clientAddress" value={formData.clientAddress} onChange={handleFormChange} placeholder="Alamat Klien" required style={styles.textarea} />
                </div>
                
                 <div style={styles.section}>
                    <h4>Detail Invoice</h4>
                    <div style={{display: 'flex', gap: '20px'}}>
                        <div style={{flex: 1}}>
                            <label>Jatuh Tempo</label>
                            <input name="dueDate" value={formData.dueDate} onChange={handleFormChange} type="date" required style={styles.input} />
                        </div>
                        <div style={{flex: 1}}>
                            <label>Status</label>
                            <select name="status" value={formData.status} onChange={handleFormChange} style={styles.input}>
                                <option value="Draft">Draft</option>
                                <option value="Sent">Terkirim</option>
                                <option value="Paid">Lunas</option>
                                <option value="Overdue">Jatuh Tempo</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div style={styles.section}>
                    <h4>Daftar Item</h4>
                    {formData.items.map((item, index) => (
                        <div key={index} style={styles.itemRow}>
                            <input name="description" value={item.description} onChange={(e) => handleItemChange(index, e)} placeholder="Deskripsi Item" required style={{...styles.input, flex: 3}} />
                            <input name="quantity" value={item.quantity} onChange={(e) => handleItemChange(index, e)} placeholder="Qty" type="number" min="1" required style={{...styles.input, flex: 1}} />
                            <input name="price" value={item.price} onChange={(e) => handleItemChange(index, e)} placeholder="Harga" type="number" min="0" required style={{...styles.input, flex: 1}} />
                            <button type="button" onClick={() => removeItem(index)} style={styles.removeButton}>Hapus</button>
                        </div>
                    ))}
                    <button type="button" onClick={addItem} style={styles.addButton}>+ Tambah Item</button>
                </div>

                <div style={{...styles.section, ...styles.summarySection}}>
                    <div style={styles.summaryLine}>
                        <span>Subtotal</span>
                        <span>{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totals.subTotal)}</span>
                    </div>
                     <div style={{...styles.summaryLine, alignItems: 'center'}}>
                        <span>Pajak (%)</span>
                        <input name="tax" value={formData.tax} onChange={handleFormChange} type="number" style={{...styles.input, width: '80px', textAlign: 'right'}} />
                    </div>
                    <hr style={styles.hr}/>
                    <div style={{...styles.summaryLine, fontWeight: 'bold', fontSize: '1.2em'}}>
                        <span>Total</span>
                        <span>{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totals.total)}</span>
                    </div>
                </div>
                
                 <div style={styles.section}>
                    <h4>Catatan (Opsional)</h4>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange} placeholder="Contoh: Informasi pembayaran" style={styles.textarea} />
                </div>

                {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

                <div style={styles.actions}>
                    <button type="button" onClick={() => navigate('/invoices')} style={styles.cancelButton}>Batal</button>
                    <button type="submit" disabled={loading} style={styles.submitButton}>
                        {loading ? 'Menyimpan...' : (isEditMode ? 'Simpan Perubahan' : 'Simpan Invoice')}
                    </button>
                </div>
            </form>
        </div>
    );
};

const styles = {
    form: { background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxWidth: '800px', margin: '0 auto'},
    section: { marginBottom: '25px' },
    input: { width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', marginTop: '5px' },
    textarea: { width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', marginTop: '5px', minHeight: '80px' },
    itemRow: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' },
    addButton: { padding: '10px 15px', background: '#e0f2f1', color: '#004d40', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    removeButton: { padding: '8px 12px', background: '#ffebee', color: '#c62828', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    summarySection: { background: '#f1f5f9', padding: '20px', borderRadius: '8px' },
    summaryLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    hr: { border: 'none', borderTop: '1px solid #ccc', margin: '10px 0'},
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
    cancelButton: { padding: '12px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    submitButton: { padding: '12px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
};

export default InvoiceForm;
